import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAllLinkData } from "../../ApiController/ApiService";
import { toast } from "react-toastify";
import { ExternalLink } from "lucide-react";

const LinkDataPage = () => {
  const { t } = useTranslation();

  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchLinks = async () => {
    setIsLoading(true);
    try {
      const res = await getAllLinkData();
      const dataArray =
        Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setLinks(dataArray);
    } catch (err) {
      toast.error(
        t("linkData.messages.errorFetch", "Could not load link data.")
      );
      setLinks([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  // pagination
  const totalPages = Math.ceil(links.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentLinks = links.slice(
    indexOfLastItem - itemsPerPage,
    indexOfLastItem
  );

  return (
    <div className="p-4 md:p-6 text-white">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl md:text-2xl font-semibold">
          {t("linkData.title", "Link Data")}
        </h1>
        <div className="text-sm text-gray-400">
          {links.length} {t("common.items", "items")}
        </div>
      </div>

      {/* Card */}
      <div className="bg-[#1D2461] rounded-2xl shadow-lg border border-[#2A2F7A] overflow-hidden">
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#B2BAFF] text-[#202224] uppercase text-[13px]">
              <tr>
                <th className="px-4 py-3 text-left">
                  {t("linkData.table.header.name", "Link Name")}
                </th>
                <th className="px-4 py-3 text-left">
                  {t("linkData.table.header.url", "URL")}
                </th>
                <th className="px-4 py-3 w-24 text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="p-6 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : currentLinks.length > 0 ? (
                currentLinks.map((item, i) => (
                  <tr
                    key={item._id || i}
                    className="border-t border-[#2A2F7A] hover:bg-[#252C77] transition"
                  >
                    {/* Name */}
                    <td className="px-4 py-3 font-medium text-gray-200">
                      {item.name || "—"}
                    </td>

                    {/* Link */}
                    <td className="px-4 py-3">
                      {item.link ? (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2A306C] text-blue-300 text-xs">
                          {item.link.length > 40
                            ? item.link.slice(0, 40) + "..."
                            : item.link}
                        </span>
                      ) : (
                        <span className="text-gray-500">N/A</span>
                      )}
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3 text-center">
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 hover:bg-blue-500 transition"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="p-10 text-center text-gray-400">
                    No link data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#2A2F7A] text-sm text-gray-400">
          <div>
            Showing {currentLinks.length} of {links.length}
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1.5 rounded-md bg-gray-800 disabled:opacity-40 hover:bg-[#2A306C]"
            >
              Prev
            </button>

            <span className="text-gray-300">
              {currentPage} / {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-md bg-gray-800 disabled:opacity-40 hover:bg-[#2A306C]"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkDataPage;